/**
 * wnKit.js — design tokens + shared primitives for the What's New module
 * (RW-1642). Follows the app system: Jost UI, Fraunces for the hero line only,
 * warm cream canvas, white cards, coral/indigo/moss tag accents, gold roadmap.
 * Provenance lines are ALWAYS monospace and quiet — the machine-written receipt
 * is the point of the page; keep it visible everywhere an entry renders.
 * Brand hexes inlined at module scope (TDZ — Gotcha #11).
 */
import React from 'react';

export const SANS = "'Jost', sans-serif";
export const SERIF = "'Fraunces', Georgia, serif";
export const MONO = "'SFMono-Regular', 'Menlo', 'Consolas', monospace";

export const WN = {
  canvas: '#F0EEE9',
  white: '#FFFFFF',
  line: '#E8E2DA',
  line2: '#F3EEE6',
  panel: '#FAF7F2',
  text: '#2A2622',
  soft: '#4A4F55',
  muted: '#6B7280',
  quiet: '#9CA3AF',
  coral: '#EE8172',
  coralDark: '#D4685A',
  coralInk: '#A63F33', // RW-2960: AA text on light surfaces (== colors.coralInk); coralDark stays for fills/hover
  coralSoft: '#FDF0EE',
  indigo: '#4F46E5',
  indigoSoft: '#EEF0FD',
  moss: '#3B5D44',
  mossSoft: '#EBF1EC',
  gold: '#C89B3C',
  goldSoft: '#F9F1DD',
};

// Tag → color system: New = coral, Improved = indigo, Fixed = moss.
export const TAG_META = {
  new:      { label: 'New',      color: '#A63F33', bg: '#FDF0EE' },
  improved: { label: 'Improved', color: '#4F46E5', bg: '#EEF0FD' },
  fixed:    { label: 'Fixed',    color: '#3B5D44', bg: '#EBF1EC' },
};

// Module → display + where the feature lives. chipKey is the Sidebar item key
// the NEW chip renders on (null = no sub-rail item, e.g. standalone FLOW —
// those still get the count pill + spotlight). route drives the "used it"
// v1 heuristic: first visit to the route clears the chip. Server twin of the
// vocabulary: functions/src/domain/changelog.js MODULES.
export const MODULE_META = {
  // Home is a standalone rail tile, not an item row — it has no `key` for the
  // NEW chip to land on (Sidebar matches highlights by item.key), so this is
  // spotlight-only, the same shape as flow/academy. A chipKey here would be a
  // silently dead highlight, not a chip.
  dashboard:   { label: 'Dashboard',    chipKey: null,             route: '/dashboard' },
  sign:        { label: 'Sign',         chipKey: 'SignInbox',      route: '/sign' },
  flow:        { label: 'FLOW',         chipKey: null,             route: '/ninja-flow' },
  comingsoon:  { label: 'Coming Soon',  chipKey: 'ComingSoon',     route: '/connect/coming-soon' },
  feed:        { label: 'Feed',         chipKey: 'Feed',           route: '/feed' },
  events:      { label: 'Events',       chipKey: 'Events',         route: '/events' },
  compliance:  { label: 'Compliance',   chipKey: 'DealsReview',    route: '/deals/review' },
  onboarding:  { label: 'Onboarding',   chipKey: 'OnboardingPipeline', route: '/onboarding' },
  contacts:    { label: 'Contacts',     chipKey: 'Contacts',       route: '/contacts' },
  search:      { label: 'Search',       chipKey: 'ConnectSearch',  route: '/connect/search' },
  groups:      { label: 'Groups',       chipKey: 'Groups',         route: '/groups' },
  deals:       { label: 'Deals',        chipKey: 'OnePipeline',    route: '/deals' },
  academy:     { label: 'Academy',      chipKey: null,             route: '/academy' },
  recognition: { label: 'Recognition',  chipKey: 'Recognition',    route: '/recognition' },
  vendors:     { label: 'Vendors',      chipKey: 'Vendors',        route: '/vendors' },
  messages:    { label: 'Messages',     chipKey: 'Messages',       route: '/messages' },
  listings:    { label: 'Listings',     chipKey: 'Listings',       route: '/listings' },
  buyers:      { label: 'Buyers',       chipKey: 'Buyers',         route: '/buyers' },
  documents:   { label: 'Documents',    chipKey: 'Documents',      route: '/documents' },
  people:      { label: 'People',       chipKey: 'People',         route: '/people' },
};

export const ROADMAP_STATUS = {
  build:     { label: 'In build',  color: '#C89B3C', bg: '#F9F1DD' },
  next:      { label: 'Next up',   color: '#4A4F55', bg: '#F3EEE6' },
  exploring: { label: 'Exploring', color: '#9CA3AF', bg: '#F7F5F1' },
};

/* ── date/week helpers ──────────────────────────────────────────────── */

export function fmtDate(isoString, opts) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', opts || { month: 'short', day: 'numeric' });
}

/** Monday of the entry's week (local) → stable key + label. */
export function weekStart(isoString) {
  const d = new Date(isoString);
  const day = (d.getDay() + 6) % 7; // Mon=0
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day);
  return d;
}

export function weekKey(isoString) {
  return weekStart(isoString).toISOString().slice(0, 10);
}

export function weekLabel(key) {
  const start = new Date(key + 'T00:00:00');
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const now = new Date();
  if (now >= start && now <= new Date(end.getTime() + 86399999)) return 'This week';
  const lastStart = new Date(start); lastStart.setDate(lastStart.getDate() + 7);
  if (now >= lastStart && now - lastStart < 7 * 86400000) return 'Last week';
  const sameYear = start.getFullYear() === now.getFullYear();
  const f = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${f(start)} – ${f(end)}${sameYear ? '' : `, ${start.getFullYear()}`}`;
}

/** Group entries (already newest-first) into [{ key, label, summary, entries }]. */
export function groupByWeek(entries) {
  const groups = [];
  const byKey = new Map();
  for (const e of entries) {
    const k = weekKey(e.publishedAt);
    if (!byKey.has(k)) {
      const g = { key: k, label: weekLabel(k), entries: [] };
      byKey.set(k, g);
      groups.push(g);
    }
    byKey.get(k).entries.push(e);
  }
  for (const g of groups) g.summary = countsSummary(g.entries);
  return groups;
}

/** "2 new · 1 improved · 1 fixed" */
export function countsSummary(entries) {
  const counts = { new: 0, improved: 0, fixed: 0 };
  for (const e of entries) if (counts[e.tag] != null) counts[e.tag] += 1;
  return [
    counts.new ? `${counts.new} new` : null,
    counts.improved ? `${counts.improved} improved` : null,
    counts.fixed ? `${counts.fixed} fixed` : null,
  ].filter(Boolean).join(' · ') || 'quiet week';
}

/* ── primitives ─────────────────────────────────────────────────────── */

export function TagPill({ tag }) {
  const m = TAG_META[tag] || TAG_META.improved;
  return (
    <span style={{
      display: 'inline-block', fontFamily: SANS, fontSize: 11, fontWeight: 700,
      letterSpacing: '.04em', textTransform: 'uppercase', color: m.color,
      background: m.bg, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>{m.label}</span>
  );
}

export function ModuleChip({ module, active, onClick }) {
  const label = MODULE_META[module]?.label || module;
  const base = {
    display: 'inline-block', fontFamily: SANS, fontSize: 12, fontWeight: 600,
    color: active ? '#fff' : '#4A4F55',
    background: active ? '#2A2622' : '#fff',
    border: `1px solid ${active ? '#2A2622' : '#E8E2DA'}`,
    padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap',
  };
  if (!onClick) return <span style={base}>{label}</span>;
  return (
    <button type="button" onClick={onClick} style={{ ...base, cursor: 'pointer' }}>
      {label}
    </button>
  );
}

/** The machine-written receipt. Deliberate, always monospace, always quiet. */
export function Provenance({ sourceRef, style }) {
  return (
    <span style={{
      fontFamily: MONO, fontSize: 11, color: '#9CA3AF',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...style,
    }} title={sourceRef}>{sourceRef}</span>
  );
}

export function RoadmapChip({ status }) {
  const m = ROADMAP_STATUS[status] || ROADMAP_STATUS.exploring;
  return (
    <span style={{
      display: 'inline-block', fontFamily: SANS, fontSize: 10, fontWeight: 700,
      letterSpacing: '.05em', textTransform: 'uppercase', color: m.color,
      background: m.bg, padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>{m.label}</span>
  );
}

/** Coral unread dot for entries newer than the reader's last visit. */
export function UnreadDot() {
  return (
    <span aria-label="Unread" style={{
      display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
      background: '#EE8172', flexShrink: 0,
    }} />
  );
}
